import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import ConfirmationSection from "./sections/confirmation";
import ReviewEntriesSection from "./sections/review-entries";
import ImportSection from "./sections/import";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {parseDelimitedList} from "../../../views/panel/operator/settings/import/engine/transformers/shared/inventory-availability-validation";

const initialReviewSectionState = {
    addItems: [],
    removeItems: [],
    successfulResults: [],
    invalidEntries: [],
    parsedRows: [],
    preventativeIssues: [],
    userSkippedRowIndices: [],
    addReasonAndNote: {
        reason: null,
        note: '',
    },
    removeReasonAndNote: {
        reason: null,
        note: '',
    },
}

const initialConfirmationSectionState = {
    successfulResults: [],
    failedResults: [],
}

/**
 * Different steps for creating quick orders dialog
 */
export const ImportAdjustmentSteps = {
    importSection: 'importSection',
    reviewSection: 'reviewSection',
    confirmationSection: 'confirmationSection',
}

const ImportAdjustmentDialog = ({open, close, onSuccess}) => {
    const [status, setStatus] = useState(ImportAdjustmentSteps.importSection);
    const [reviewSectionState, setReviewSectionState] = useState(initialReviewSectionState);
    const [confirmationSectionState, setConfirmationSectionState] = useState(initialConfirmationSectionState);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    /**@type {Array<ImportCsvFileRequiredField>}*/
    const fields = useMemo(() => [
        {
            name: 'warehouseCode',
            title: 'Warehouse Code',
            generator: () => warehouses?.find(e => e.isDefault)?.code ?? `"00"`,
            format: (e) => {
                const code = e === '"00"' ? '00' : e;
                return warehouses.find(e => e.code === code) ? code : null;
            },
        },
        {
            name: 'partNo',
            title: 'Part number',
            generator: () => '',
            format: (e) => e?.toString()?.toUpperCase()?.trim(),
        },
        {
            name: 'quantity',
            title: 'Quantity',
            generator: () => 0,
            format: (e) => {
                const parsed = Number(e);
                return Number.isNaN(parsed) ? 0 : parsed;
            },
        },
        {
            name: 'cost',
            title: 'Cost',
            generator: () => "NULL",
            format: (e) => {
                e = e?.toUpperCase()?.trim();
                if (e === "NULL")
                    return null;
                const num = Number(e);
                if (Number.isNaN(num))
                    return null;
                return Math.max(num, 0);
            },
        },
        {
            name: 'serialNos',
            title: 'Serial numbers',
            optional: true,
            generator: () => '',
            format: (e) => parseDelimitedList(e),
        }
    ], [warehouses])

    /**
     * Fetches the warehouses for the operator.
     * @type {(function(): Promise<void>)}
     */
    const fetchWarehouses = useCallback(async () => {
        setLoading(true);
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setWarehouses(response.data);
        }
    }, [isMounted])

    /**
     * Fetches the warehouses as soon as the component mounts.
     */
    useEffect(() => {
        if (!open)
            return;
        void fetchWarehouses();
    }, [fetchWarehouses, open])


    /**
     * Resets the state of this dialog to its initial values.
     */
    const resetState = useCallback(() => {
        setStatus(ImportAdjustmentSteps.importSection);
        setReviewSectionState(initialReviewSectionState);
        setConfirmationSectionState(initialConfirmationSectionState);
    }, [])

    /**
     * As soon as the component mounts:
     * - sets the selected user if we are in the user-based views (customer panel).
     */
    useEffect(() => {
        if (!open) {
            resetState();
        }
    }, [open, resetState])

    const dialogSize = useMemo(() => {
        switch (status) {
            case ImportAdjustmentSteps.importSection:
            default:
                return 'sm';
            case ImportAdjustmentSteps.reviewSection:
                return 'md';
            case ImportAdjustmentSteps.confirmationSection:
                return 'sm';
        }
    }, [status])

    /**
     * Callback function to be called after the adjustments have been created.
     * @type {(function(*): void)}
     */
    const onAdjustmentsCreated = useCallback((res) => {
        setStatus(ImportAdjustmentSteps.confirmationSection);
        setConfirmationSectionState({
            successfulResults: res.filter(e => e.resultFlag),
            failedResults: res.filter(e => !e.resultFlag),
        });
        onSuccess(res.filter(e => e.resultFlag));
    }, [onSuccess])

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={dialogSize}
            className={'bound-dialog import-adjustment-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            {
                status === ImportAdjustmentSteps.importSection &&
                <ImportSection
                    setReviewSectionState={setReviewSectionState}
                    setStatus={setStatus}
                    fields={fields}
                    loading={loading}
                />
            }
            {
                status === ImportAdjustmentSteps.reviewSection &&
                <ReviewEntriesSection
                    state={reviewSectionState}
                    setState={setReviewSectionState}
                    resetState={resetState}
                    onSuccess={onAdjustmentsCreated}
                />
            }
            {
                status === ImportAdjustmentSteps.confirmationSection &&
                <ConfirmationSection
                    state={confirmationSectionState}
                    reviewState={reviewSectionState}
                    fields={fields}
                    resetState={resetState}
                />
            }
        </Dialog>
    );
}


export default ImportAdjustmentDialog;